import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import { forkJoin, Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap, take, defaultIfEmpty, filter } from 'rxjs/operators';

import {ChiliGrafixConfig, ChiliPreviewResult} from '../interfaces/chili-grafix';

import {API_URL} from '../constants/api-urls';
import {CHILI_PREVIEW_MAX_RETRIES, CHILI_PREVIEW_RETRY_DELAY_MS} from '../constants/globals';
import {Response} from '../models/response.model';


@Injectable()
export class ChiliGrafixService {

  config: ChiliGrafixConfig;

  constructor(private http: HttpClient) {
  }

  // get chilli configuration and auth token
  // This method combines the fetching of the chilli configuration and the auth token into a single observable
  // that emits a ChiliGrafix object containing both pieces of information.
  // If either the configuration or the auth token is not available, it returns null.
  // If an error occurs during the fetching process, it logs the error and returns null.
  public getChiliConfig(): Observable<ChiliGrafixConfig | null> {
    // This method should return an Observable that emits the chilli configuration.

    return forkJoin([this.fetchChiliConfiguration(), this.getChiliAuthToken()]).pipe(
      map((data) => {
        if (!data || data.length !== 2 || !data[0] || !data[1]) {
          console.error('Invalid data received from fetchChiliConfiguration or getChiliAuthToken');
          return null;
        }

        this.config = {
          environmentBaseURL: data[0].environmentBaseURL,
          userInterfaceID: data[0].userInterfaceID,
          authToken: data[1]
        };

        return this.config;
      }),
      catchError((err) => {
        console.error('Error fetching chili configuration or auth token:', err);
        return of(null); // Return null or handle the error as needed
      })
    )
  }

  // fetch chili  configuration from api
  private fetchChiliConfiguration(): Observable<Partial<ChiliGrafixConfig> | null> {

    // return cached config
    if (this.config) {
      return of(this.config);
    }

    const url = `${API_URL}/chili/config`;
    return this.http.get<Response>(url).pipe(
      map(result => {

        if (result.data) {
          const {base_url, user_interface_id} = result.data;
          return {
            environmentBaseURL: base_url,
            userInterfaceID: user_interface_id,
          }
        } else {
          console.error('Chili configuration is empty or not defined');
          return null;
        }
      }),
      catchError(this.handleError)
    );
  }


  // this method  is used  to get the Chili auth token and refresh it if necessary.
  // It returns an Observable that emits the auth token as a string.
  // If the token is not available, it returns an error.
  // If an error occurs during the fetching process, it logs the error and returns an observable
  // that emits an error message.
  public getChiliAuthToken(): Observable<string> {
    const url = `${API_URL}/tokens/chili`;
    return this.http.get<Response>(url).pipe(
      map(response => {

        if (response.data) {
          return response.data
        }

        return new Error(`Could not get chili auth token`);
      }),
      catchError(this.handleError)
    );
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'An unknown error occurred!';
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred.
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      errorMessage = `Server returned code ${error.status}, error message is: ${error.message}`;
    }
    console.error(errorMessage);
    // Return an observable with a user-facing error message.
    return of(null);
  }

  public getChiliProjectIdForProductId(productId: number): Observable<string> {
    if (!productId) {
      return of(null);
    }

    const body = {
      product_id: productId,
    }

    return this.http.post<Response>(`${API_URL}/chili/project`, body).pipe(
      map(result => {

        if (!result?.data) {
          return '';
        }

        return result?.data?.project_id || '';
      }),
      catchError(this.handleError)
    )

  }

  public createChiliPreview(projectId: string, chiliData: string): Observable<ChiliPreviewResult | null> {
    if (!projectId || !chiliData) {
      return of(null);
    }

    const url = `${API_URL}/chili/project/preview`;

    return this.http.post<Response>(url, {
      project_id: projectId,
      chili_data: chiliData,
    }).pipe(
      switchMap(result => {
        // If Chili returned a taskId (HTTP 202), poll until ready.
        if (result?.data?.task_id) {
          return this.pollPreviewUntilReady(url, projectId, result.data.task_id);
        }

        // Got an immediate result (200) — return the preview.
        const preview = result?.data as ChiliPreviewResult | null;
        if (!preview?.chili_output || !preview?.chili_output_thumbnail) {
          return of(null);
        }
        return of(preview);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Polls the preview endpoint with a taskId until the image is ready or
   * the maximum number of retries is exhausted. Each attempt is a single
   * non-blocking HTTP call — no usleep() on the server side.
   */
  private pollPreviewUntilReady(url: string, projectId: string, taskId: string): Observable<ChiliPreviewResult | null> {
    return timer(0, CHILI_PREVIEW_RETRY_DELAY_MS).pipe(
      switchMap(() => this.http.post<Response>(url, { project_id: projectId, task_id: taskId })),
      map((result) => {
        const preview = result?.data as ChiliPreviewResult | null;

        // Got the image — return it.
        if (preview?.chili_output && preview?.chili_output_thumbnail) {
          return preview;
        }

        // Still waiting (task_id still present or null body) — signal retry.
        if (result?.data?.task_id || !preview?.chili_output) {
          return undefined;
        }

        // No task_id and no image — give up.
        return null;
      }),
      take(CHILI_PREVIEW_MAX_RETRIES),
      filter((v: ChiliPreviewResult | null | undefined): v is ChiliPreviewResult | null => v !== undefined),
      defaultIfEmpty(null as ChiliPreviewResult | null),
      take(1),
    );
  }

  public getChiliProjectDocument(environmentBaseURL?: string, projectId?: string, authToken?: string): Observable<string | null> {
    if (!environmentBaseURL || !projectId || !authToken) {
      return of(null);
    }

    const baseURL = environmentBaseURL.replace(/\/$/, '');

    return this.http.get(
      `${baseURL}/projects/${projectId}/document`,
      { headers: { Authorization: `Bearer ${authToken}` }, responseType: 'text' }
    ).pipe(
      catchError(() => of(null))
    );
  }

}


